import { useEffect, useRef, useState } from 'react';

import { PageHeader } from '@/components/app-shell/PageHeader';
import { useAuthStore } from '@/features/auth/auth-store';
import { useToast } from '@/features/campaigns/toast-context';

import { UploadModal } from './UploadModal';
import { useVaultDelete, useVaultDocuments, useVaultUpload, type StagedUrl } from './useVault';
import { vaultDownloadUrl, type VaultDocument } from './vault-api';
import { fileExt, formatDate, formatSize } from './vault-format';
import { KnowledgeStatusChip } from './KnowledgeStatusChip';
import { useKnowledgeStatus } from './useKnowledgeStatus';

/**
 * The v7 Vault page (SCRUM-374): tenant document repository over the real
 * /api/vault backend (SCRUM-373). Header count + drop hint, expandable
 * search, type-filter panel, the 5-column table with the v7 hover treatment,
 * per-row delete with a confirm modal, the upload modal, and a page-level
 * drag-drop overlay.
 *
 * Documented v7 deviations (scope agreed on SCRUM-374):
 * - Row click triggers the REAL download (302 → presigned COS URL / source
 *   URL) instead of the prototype's mock preview modal — no PDF renderer yet.
 * - The Source (IQ/Bank) filter is omitted: it keys on a `direction` field
 *   the documents table deliberately does not model.
 * - Read-only gating (isVaultReadOnly = role 'Bank Officer') is UI-only until
 *   SCRUM-102/103 land server-side identity — stated in VAULT.md.
 */
export function VaultPage() {
  const user = useAuthStore((s) => s.user);
  // SCRUM-495: live Knowledge Store index status (snapshot + SSE deltas).
  const { statusOf } = useKnowledgeStatus();
  const { showToast } = useToast();
  const { documents, isLoading, isError } = useVaultDocuments();
  const uploadMutation = useVaultUpload();
  const deleteMutation = useVaultDelete();

  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<VaultDocument | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const dragCounter = useRef(0);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // v7 isVaultReadOnly: the Bank Officer role is view-only. UI gate ONLY —
  // no server-side authorization until SCRUM-102/103.
  const readOnly = user?.role === 'Bank Officer';

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  useEffect(() => {
    if (!pendingDelete) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPendingDelete(null);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [pendingDelete]);

  const extTypes = [...new Set(documents.map((d) => fileExt(d.fileName)).filter(Boolean))].sort();

  const query = search.toLowerCase().trim();
  const filtered = documents.filter((d) => {
    if (typeFilter !== 'all' && fileExt(d.fileName) !== typeFilter) return false;
    if (
      query &&
      !d.fileName.toLowerCase().includes(query) &&
      !d.uploadedBy.toLowerCase().includes(query)
    ) {
      return false;
    }
    return true;
  });

  const uploaderIdentity = user?.email ?? user?.displayName ?? 'Unknown';

  const commitUpload = async (files: File[], urls: StagedUrl[]) => {
    const created = await uploadMutation.mutateAsync({ files, urls, uploadedBy: uploaderIdentity });
    setUploadOpen(false);
    showToast({
      title: 'Added to Vault',
      body: `${String(created.length)} document${created.length === 1 ? '' : 's'} added.`,
      variant: 'success',
    });
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;
    const doc = pendingDelete;
    setPendingDelete(null);
    deleteMutation.mutate(doc.id, {
      onSuccess: () => {
        showToast({ title: 'File deleted', body: `${doc.fileName} removed from the Vault.` });
      },
      onError: (e) => {
        showToast({ title: 'Delete failed', body: e.message });
      },
    });
  };

  /** Row click → the real download: 302 to a presigned COS / source URL. */
  const openDocument = (doc: VaultDocument) => {
    // SCRUM-643: the download opens as a top-level navigation (no Bearer), so the
    // authenticated tenant rides the query param. Guard on an absent tenant.
    if (!user?.tenantId) return;
    window.open(vaultDownloadUrl(doc.id, user.tenantId), '_blank', 'noopener');
  };

  const stageDroppedFiles = (files: FileList) => {
    if (files.length === 0) return;
    void uploadMutation
      .mutateAsync({ files: Array.from(files), urls: [], uploadedBy: uploaderIdentity })
      .then((created) => {
        showToast({
          title: 'Added to Vault',
          body: `${String(created.length)} document${created.length === 1 ? '' : 's'} added.`,
          variant: 'success',
        });
      })
      .catch((e: unknown) => {
        showToast({
          title: 'Upload failed',
          body: e instanceof Error ? e.message : 'Please try again.',
        });
      });
  };

  return (
    <div className="flex min-h-full flex-col">
      <PageHeader title="Vault" subtitle="Secure document repository" />

      {/* Drop zone: the drag handlers + the absolute-inset overlay stay on THIS
          content div (below the header band) — you drop onto the content, not the
          header. `relative` scopes the overlay; `flex-1` fills below the band.
          Carries data-testid="vault-page" as the interactive page surface. */}
      <div
        className="relative flex-1 p-4"
        data-testid="vault-page"
        onDragEnter={
          readOnly
            ? undefined
            : (e) => {
                e.preventDefault();
                dragCounter.current += 1;
                setDragActive(true);
              }
        }
        onDragOver={
          readOnly
            ? undefined
            : (e) => {
                e.preventDefault();
              }
        }
        onDragLeave={
          readOnly
            ? undefined
            : (e) => {
                e.preventDefault();
                dragCounter.current -= 1;
                if (dragCounter.current <= 0) {
                  dragCounter.current = 0;
                  setDragActive(false);
                }
              }
        }
        onDrop={
          readOnly
            ? undefined
            : (e) => {
                e.preventDefault();
                dragCounter.current = 0;
                setDragActive(false);
                stageDroppedFiles(e.dataTransfer.files);
              }
        }
      >
        {/* Toolbar: count + drop hint (left), search / filter / upload (right) */}
        <div className="mb-4 flex items-start justify-between">
          <div>
            <div className="text-text-2 font-mono text-xs">
              {String(documents.length)} document{documents.length === 1 ? '' : 's'}
            </div>
            <div className="text-text-3 mt-0.5 text-xs">
              {readOnly ? 'View only' : 'Drag and drop files anywhere on this page to upload'}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Expandable search */}
            <div className="flex items-center">
              <button
                type="button"
                aria-label="Search documents"
                aria-expanded={searchOpen}
                onClick={() => {
                  setSearchOpen((open) => {
                    if (open) setSearch('');
                    return !open;
                  });
                }}
                className={`border-border-1 bg-bg-3 rounded-tk-md grid h-[38px] w-[38px] place-items-center border transition-colors ${
                  searchOpen
                    ? 'border-brand-primary text-brand-primary'
                    : 'text-text-2 hover:border-brand-primary hover:text-brand-primary'
                }`}
              >
                <svg
                  viewBox="0 0 16 16"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <circle cx="7" cy="7" r="4" />
                  <line x1="10" y1="10" x2="13.5" y2="13.5" />
                </svg>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${searchOpen ? 'w-[232px] opacity-100' : 'w-0 opacity-0'}`}
              >
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search documents…"
                  aria-label="Search documents input"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                  }}
                  className="border-border-1 bg-bg-3 text-text-0 rounded-tk-md focus:border-brand-primary ml-2 h-[38px] w-[220px] border px-3 text-[0.8125rem] outline-none"
                />
              </div>
            </div>

            {/* Filter toggle */}
            <button
              type="button"
              aria-label="Filter documents"
              aria-expanded={filterPanelOpen}
              onClick={() => {
                setFilterPanelOpen((v) => !v);
              }}
              className={`rounded-tk-md bg-bg-3 relative grid h-[38px] w-[38px] place-items-center border transition-colors ${
                filterPanelOpen || typeFilter !== 'all'
                  ? 'border-brand-primary text-brand-primary'
                  : 'border-border-1 text-text-2'
              }`}
            >
              <svg
                viewBox="0 0 16 16"
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M2 3h12M4 8h8M6 13h4" />
              </svg>
              {typeFilter !== 'all' && (
                <span className="bg-brand-primary absolute top-1 right-1 h-1.5 w-1.5 rounded-full" />
              )}
            </button>

            {!readOnly && (
              <button
                type="button"
                onClick={() => {
                  setUploadOpen(true);
                }}
                className="bg-brand-primary text-bg-0 rounded-tk-md flex items-center gap-1.5 px-4 py-2 text-[0.8125rem] font-semibold"
              >
                <svg
                  viewBox="0 0 16 16"
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <line x1="8" y1="3" x2="8" y2="13" />
                  <line x1="3" y1="8" x2="13" y2="8" />
                </svg>
                Upload
              </button>
            )}
          </div>
        </div>

        {/* Type filter panel */}
        {filterPanelOpen && (
          <div className="mb-4 flex flex-col gap-1.5">
            <span className="text-text-3 text-[0.6875rem]">Type</span>
            <div className="flex flex-wrap gap-2">
              {['all', ...extTypes].map((ext) => {
                const active = typeFilter === ext;
                return (
                  <button
                    key={ext}
                    type="button"
                    onClick={() => {
                      setTypeFilter(ext);
                    }}
                    className={`rounded-full border px-3 py-1 font-mono text-[0.6875rem] font-semibold tracking-wide uppercase transition-colors ${
                      active
                        ? 'border-brand-primary bg-brand-dim text-brand-primary'
                        : 'border-border-1 bg-bg-4 text-text-2'
                    }`}
                  >
                    {ext === 'all' ? 'All' : ext}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Table / states */}
        {isLoading ? (
          <div className="text-text-3 py-16 text-center text-sm">Loading documents…</div>
        ) : isError ? (
          <div className="text-text-3 py-16 text-center text-sm">
            The Vault is unreachable right now — try again shortly.
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-text-3 py-16 text-center text-sm">
            {documents.length === 0
              ? 'No documents yet — drag a file here or click Upload to get started.'
              : 'No matching documents in the vault.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-separate border-spacing-0">
              <thead>
                <tr>
                  <th className="text-text-3 px-2 pb-2 pl-3 text-left font-mono text-[0.6875rem] font-semibold tracking-wide uppercase">
                    File
                  </th>
                  <th className="text-text-3 w-[100px] px-2 pb-2 text-left font-mono text-[0.6875rem] font-semibold tracking-wide uppercase">
                    Size
                  </th>
                  <th className="text-text-3 w-[140px] px-2 pb-2 text-left font-mono text-[0.6875rem] font-semibold tracking-wide uppercase">
                    Uploaded
                  </th>
                  <th className="text-text-3 w-[140px] px-2 pb-2 text-left font-mono text-[0.6875rem] font-semibold tracking-wide uppercase">
                    Uploaded by
                  </th>
                  <th className="text-text-3 w-[120px] px-2 pb-2 text-left font-mono text-[0.6875rem] font-semibold tracking-wide uppercase">
                    Knowledge
                  </th>
                  {!readOnly && <th className="w-12 pb-2" aria-label="Actions" />}
                </tr>
              </thead>
              <tbody>
                {filtered.map((doc) => (
                  <tr
                    key={doc.id}
                    className="group hover:bg-brand-dim cursor-pointer transition-colors"
                    onClick={() => {
                      openDocument(doc);
                    }}
                  >
                    <td className="text-text-0 group-hover:text-brand-primary rounded-l-md px-2 py-2.5 pl-3 text-[0.8125rem] font-medium transition-colors">
                      {doc.fileName}
                    </td>
                    <td className="text-text-2 px-2 py-2.5 font-mono text-xs">{formatSize(doc)}</td>
                    <td className="text-text-2 px-2 py-2.5 text-xs">{formatDate(doc.createdAt)}</td>
                    <td className="text-text-2 px-2 py-2.5 text-xs">{doc.uploadedBy || '—'}</td>
                    <td className="px-2 py-2.5">
                      <KnowledgeStatusChip entry={statusOf(doc.id)} />
                    </td>
                    {!readOnly && (
                      <td className="rounded-r-md px-2 py-2.5">
                        <button
                          type="button"
                          title="Remove"
                          aria-label={`Remove ${doc.fileName}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setPendingDelete(doc);
                          }}
                          className="text-text-3 hover:text-danger rounded-tk-sm p-1 transition-colors"
                        >
                          <svg
                            viewBox="0 0 16 16"
                            width="14"
                            height="14"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            aria-hidden="true"
                          >
                            <line x1="4" y1="4" x2="12" y2="12" />
                            <line x1="12" y1="4" x2="4" y2="12" />
                          </svg>
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Page-level drop overlay (v7 #vaultDropOverlay) */}
        {!readOnly && dragActive && (
          <div
            data-testid="vault-drop-overlay"
            className="border-brand-primary bg-brand-dim rounded-tk-lg pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 border-2 border-dashed"
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              className="text-brand-primary"
              aria-hidden="true"
            >
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <span className="text-brand-primary text-sm font-semibold">Drop to upload</span>
          </div>
        )}

        {uploadOpen && (
          <UploadModal
            onClose={() => {
              setUploadOpen(false);
            }}
            onConfirm={commitUpload}
          />
        )}

        {/* Delete confirm modal (v7 vaultDeleteModal) */}
        {pendingDelete && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40">
            <div
              role="dialog"
              aria-label="Delete file"
              className="bg-bg-4 border-border-1 rounded-tk-lg shadow-tk-lg flex w-[440px] max-w-[calc(100vw-32px)] flex-col border"
            >
              <div className="border-border-0 flex items-center justify-between border-b px-5 py-4">
                <span className="font-display text-text-0 text-base font-semibold">
                  Delete file
                </span>
                <button
                  type="button"
                  aria-label="Close"
                  onClick={() => {
                    setPendingDelete(null);
                  }}
                  className="text-text-3 hover:bg-bg-3 hover:text-text-0 rounded-tk-sm grid h-[26px] w-[26px] place-items-center"
                >
                  <svg
                    viewBox="0 0 16 16"
                    width="12"
                    height="12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    aria-hidden="true"
                  >
                    <line x1="4" y1="4" x2="12" y2="12" />
                    <line x1="12" y1="4" x2="4" y2="12" />
                  </svg>
                </button>
              </div>
              <div className="text-text-1 px-6 py-5 text-sm">
                Remove <span className="text-text-0 font-semibold">{pendingDelete.fileName}</span>{' '}
                from the Vault? This also deletes the stored copy.
              </div>
              <div className="border-border-0 flex items-center justify-end gap-2 border-t px-5 py-4">
                <button
                  type="button"
                  onClick={() => {
                    setPendingDelete(null);
                  }}
                  className="border-border-1 bg-bg-3 text-text-1 hover:text-text-0 rounded-tk-md border px-4 py-2 text-[0.8125rem] font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDelete}
                  className="bg-danger rounded-tk-md px-4 py-2 text-[0.8125rem] font-semibold text-white"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
